import { useMemo, useState } from 'react';

import { Dialog, DialogContent, Grid } from '@mui/material';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import Loader from 'src/components/Loader/Loader';
import ModalContent from 'src/components/ModalContent/ModalContent';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { MCCS_TABLE_HEADERS } from 'src/constants';
import { DELETE_MCC_MUTATION } from 'src/graphql/mutations/mccs.mutation';
import { GET_MCCS } from 'src/graphql/queries/mccs.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { filteredMccTableData, FilterOptionsType, updateTableFilters } from 'src/utils/tableUtils';

const MCCPage = () => {
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [recordId, setRecordId] = useState<number | null>();
    const [tableFilters, setTableFilters] = useState({});

    const { data, loading, refetch } = useQuery(GET_MCCS, {
        fetchPolicy: 'cache-and-network',
        onError: () => {
            return showErrorToast('There was an error retrieving Merchant Category Codes');
        },
    });

    const filterOptions = () => {
        return Object.fromEntries([
            [
                'MCC Number',
                Array.from(
                    new Set(
                        data.mccs.data?.map(({ code }) => {
                            return code;
                        })
                    )
                ).map((code) => {
                    return {
                        value: code,
                        label: code,
                    };
                }),
            ],
            [
                'MCC Friendly Name',
                Array.from(
                    new Set(
                        data.mccs.data?.map(({ name }) => {
                            return name;
                        })
                    )
                ).map((name) => {
                    return {
                        value: name,
                        label: name,
                    };
                }),
            ],
            [
                'Risk Classification',
                Array.from(
                    new Set(
                        data.mccs.data?.map(({ risk }) => {
                            return risk;
                        })
                    )
                ).map((risk) => {
                    return {
                        value: risk,
                        label: risk,
                    };
                }),
            ],
        ]) as FilterOptionsType;
    };

    const tableData = useMemo(() => {
        return filteredMccTableData(data?.mccs, tableFilters).map((mcc) => {
            return {
                ...mcc,
                risk: <StatusChip status={mcc?.risk} type="mcc" />,
            };
        });
    }, [data, tableFilters]);

    const exportData = useMemo(() => {
        if (data?.mccs?.data) {
            return data.mccs.data.map((mcc) => {
                return {
                    ...mcc,
                    risk: mcc?.risk,
                };
            });
        }

        return [];
    }, [data]);

    const [deleteMccRecord] = useMutation(DELETE_MCC_MUTATION, {
        onCompleted: (data) => {
            showSuccessToast(`${data.deleteMcc.code} deleted successfully`);
            refetch();
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Deleting the MCC');
        },
    });

    const handleAddRecord = () => {
        navigate('/mccs/add');
    };

    const handleViewRecord = ({ id }) => {
        navigate(`/mccs/${id}`);
    };

    const handleOpenDeleteModal = (id) => {
        setRecordId(id);
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    const handleDeleteRecord = () => {
        deleteMccRecord({ variables: { id: recordId } });
        handleCloseDeleteModal();
    };

    return (
        <PageContent
            heading="Merchant Category Code"
            subHeading="Add, view, update or remove Merchant Category Codes"
        >
            <MetaTags title="MCC" description="Merchant Category Code" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    {/* Display loader if data is still fetching */}
                    {loading ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add Merchant Category Code"
                            data={tableData}
                            exportData={exportData}
                            headers={MCCS_TABLE_HEADERS}
                            title="Merchant Category Code"
                            addRecordCallBack={handleAddRecord}
                            handleViewRecord={handleViewRecord}
                            deleteRecordCallBack={handleOpenDeleteModal}
                            enableFilter
                            enableExport
                            filterOptions={filterOptions()}
                            updateTableFilters={(filter: string, newFilterValue: string) => {
                                setTableFilters((prevFilters) => {
                                    return updateTableFilters(prevFilters, newFilterValue, filter);
                                });
                            }}
                        />
                    )}
                </Grid>
                <Dialog
                    open={openDeleteModal}
                    onClose={handleCloseDeleteModal}
                    aria-labelledby="delete-user-dialog-title"
                    sx={{
                        '& .MuiDialog-paper': {
                            width: '400px',
                        },
                    }}
                >
                    <DialogContent>
                        <ModalContent
                            handleClose={handleCloseDeleteModal}
                            handleAction={handleDeleteRecord}
                            header="Delete MCC?"
                            message="Are you sure you want to permanently delete this MCC?"
                            buttonText="Delete"
                        />
                    </DialogContent>
                </Dialog>
            </Grid>
        </PageContent>
    );
};

export default MCCPage;

export const TextEditorValues = (formattedData: any) => {
    return (
        <div>
            {formattedData &&
                formattedData.data.map((value: any, index: number) => (
                    <div key={index}>
                        {value.type === 'unordered-list-item' ? (
                            <ul className="list-custom-indent">
                                <li className={`${value.type}`}>{value.text}</li>
                            </ul>
                        ) : (
                            <div>{value.text}</div>
                        )}
                    </div>
                ))}
        </div>
    );
};
